import React from 'react';
import { Content } from '../../types';
import { useNavigate } from 'react-router-dom';
import { getFileTypeInfo } from '../../utils/fileUtils';
import { getCategoryPath } from '../../utils/categoryUtils';
import './TimelineItem.css';

interface Props {
  content: Content & {
    attachments?: any[];
    tags?: any[];
    topicArea?: any;
  };
  searchQuery?: string;
  categories?: any[];
}

const escapeRegExp = (str: string) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const highlightText = (text: string, query?: string) => {
  if (!query || !query.trim()) return <>{text}</>;
  
  const escapedQuery = escapeRegExp(query.trim());
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) => 
        regex.test(part) 
          ? <mark key={i} className="search-highlight">{part}</mark> 
          : part
      )}
    </>
  );
};

const TimelineItem: React.FC<Props> = ({ content, searchQuery, categories = [] }) => {
  const navigate = useNavigate();
  const attachments = content.attachments || [];
  const tags = content.tags || [];
  const topicArea = content.topicArea || null;

  // 첫 번째 첨부파일 정보 (대표 파일)
  const primaryAttachment = attachments.length > 0 ? attachments[0] : null;
  
  // 첨부파일이 이미지인지 확인
  const isImage = primaryAttachment?.file_type === '이미지' || 
                  primaryAttachment?.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  // 카테고리 경로 생성
  const categoryPaths = [
    (content as any).main_category1_id ? getCategoryPath((content as any).main_category1_id, categories) : '',
    (content as any).main_category2_id ? getCategoryPath((content as any).main_category2_id, categories) : '',
    (content as any).main_category3_id ? getCategoryPath((content as any).main_category3_id, categories) : ''
  ].filter(Boolean);

  return (
    <div className="timeline-card">
      {/* 첨부파일 표시 영역 */}
      <div className="timeline-attachment">
        {primaryAttachment ? (
          isImage ? (
            // 이미지인 경우 썸네일 표시
            <div className="attachment-image">
              <img 
                src={primaryAttachment.thumb_small 
                  ? `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${primaryAttachment.thumb_small}`
                  : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${primaryAttachment.file_url}`
                }
                alt="thumbnail"
                className="timeline-thumb"
              />
              {attachments.length > 1 && (
                <div className="attachment-count-badge">
                  +{attachments.length - 1}
                </div>
              )}
            </div>
          ) : (
            // 이미지가 아닌 경우 파일 타입 아이콘 표시
            <div className="attachment-icon">
              {(() => {
                const isLabel = !primaryAttachment.file_type?.includes('/') && 
                               !primaryAttachment.file_type?.includes('application');
                const fileInfo = isLabel 
                  ? (() => {
                      const labelIconMap: Record<string, string> = {
                        'Word 문서': '📄',
                        'Excel 문서': '📊', 
                        'PowerPoint 문서': '📽️',
                        'PDF 문서': '📋',
                        '이미지': '🖼️',
                        '비디오': '🎬',
                        '오디오': '🎵',
                        '압축 파일': '📦',
                        '텍스트': '📝'
                      };
                      return { icon: labelIconMap[primaryAttachment.file_type] || '📎' };
                    })()
                  : getFileTypeInfo(primaryAttachment.file_name, primaryAttachment.file_type);
                
                return (
                  <div className="attachment-icon-box">
                    <span className="attachment-icon-emoji">{fileInfo.icon}</span>
                    {attachments.length > 1 && (
                      <div className="attachment-count-badge">
                        +{attachments.length - 1}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )
        ) : (
          // 첨부파일이 없는 경우 썸네일이 있으면 표시
          content.thumbnail && (
            <img src={content.thumbnail} alt="thumb" className="timeline-thumb" />
          )
        )}
      </div>
      
      {/* 컨텐츠 정보 */}
      <div className="timeline-info">
        <h3>{highlightText(content.title, searchQuery)}</h3>
        
        {/* 카테고리 표시 */}
        {categoryPaths.length > 0 && (
          <div className="timeline-cat-paths">
            {categoryPaths.map((path, index) => (
              <span key={index} className="timeline-cat-path-item">
                📂 {path}
              </span>
            ))}
          </div>
        )}
        
        {/* 태그 표시 */}
        {tags.length > 0 && (
          <div className="timeline-tag-list">
            {tags.map((tag: any) => (
              <span key={tag.id} className="timeline-tag-item">
                #{highlightText(tag.name, searchQuery)}
              </span>
            ))}
          </div>
        )}
        
        {topicArea && (
          <div className="timeline-topic-link-box">
            주제영역:{' '}
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/topic-areas/${topicArea.id}`);
              }}
              className="timeline-topic-span-link"
            >
              {topicArea.title}
            </span>
          </div>
        )}
        
        <p>{highlightText(content.description, searchQuery)}</p>
        
        <div className="timeline-card-footer">
          <span className="timeline-card-footer-date">
            {new Date(content.timestamp).toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
          {attachments.length > 0 && (
            <span className="timeline-card-footer-attach">
              📎 {attachments.length}개
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelineItem;
